import {
  addRepeatItem,
  advanceFormPage,
  createInitialRepeatItems,
  evaluateFormRuntime,
  formNavigationState,
  previousFormPage,
  removeRepeatItem,
  repeatItemsFromValue,
  type FieldValidationIssue,
} from '@dispatch/form-engine';
import type { TemplateDefinition, TemplateField } from '@dispatch/schemas';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

function valueAsString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return '';
}

function choiceValues(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

interface AddressValue {
  line1: string;
  line2: string;
  townCity: string;
  county: string;
  postcode: string;
}

function addressValue(value: unknown): AddressValue {
  const record =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Readonly<Record<string, unknown>>)
      : {};
  const part = (key: keyof AddressValue): string => {
    const candidate = record[key];
    return typeof candidate === 'string' ? candidate : '';
  };
  return {
    line1: part('line1'),
    line2: part('line2'),
    townCity: part('townCity'),
    county: part('county'),
    postcode: part('postcode'),
  };
}

function systemFieldSourceLabel(source: TemplateField['systemSource']): string {
  switch (source) {
    case 'job_number':
      return 'Job number';
    case 'external_reference':
      return 'External reference';
    case 'business':
      return 'Business';
    case 'form_name':
      return 'Form name';
    case 'form_version':
      return 'Form version';
    case 'engineer':
      return 'Engineer';
    case 'coordinator':
      return 'Coordinator';
    case 'customer':
      return 'Customer';
    case 'created_at':
      return 'Created date/time';
    case 'dispatched_at':
      return 'Dispatched date/time';
    case 'started_at':
      return 'Started date/time';
    case 'completed_at':
      return 'Completed date/time';
    case 'job_status':
      return 'Job status';
    default:
      return 'System value';
  }
}

function deferredCaptureText(field: TemplateField): string {
  switch (field.type) {
    case 'file_upload':
      return field.fileUpload?.multiple
        ? `Document upload · up to ${field.fileUpload.maxFiles} files`
        : 'Document upload · one file';
    case 'single_photograph':
      return 'Photo capture · one photograph';
    case 'multiple_photographs':
      return `Photo capture · ${field.photographs?.minPhotos ?? 0}–${field.photographs?.maxPhotos ?? 10} photographs`;
    case 'signature':
      return 'Signature capture';
    case 'gps':
      return 'Capture current GPS position';
    case 'map_location':
      return 'Select a location on a map';
    default:
      return '';
  }
}

function nestedFieldIds(field: TemplateField): readonly string[] {
  if (field.type === 'repeating_section') return field.repeating?.fieldIds ?? [];
  if (field.type === 'table_grid')
    return field.grid?.columns.flatMap((column) => column.fieldIds) ?? [];
  return [];
}

function FieldShell({ field, children }: { field: TemplateField; children: ReactNode }) {
  return (
    <div className="runtime-field" data-field-id={field.id}>
      {children}
      {field.helpText ? <p className="help-text">{field.helpText}</p> : null}
    </div>
  );
}

export function FormRuntimePreview({
  definition,
  mode,
}: {
  definition: TemplateDefinition;
  mode: 'desktop' | 'mobile';
}) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [pageId, setPageId] = useState(definition.pages[0]?.id ?? '');
  const [issues, setIssues] = useState<readonly FieldValidationIssue[]>([]);

  useEffect(() => {
    setPageId((current) =>
      definition.pages.some((page) => page.id === current)
        ? current
        : (definition.pages[0]?.id ?? ''),
    );
  }, [definition.pages]);

  useEffect(() => {
    setIssues([]);
  }, [definition]);

  useEffect(() => {
    setValues((current) => {
      const next = { ...current };
      let changed = false;
      for (const field of definition.fields) {
        if (field.type !== 'repeating_section' || !field.repeating) continue;
        const existing = repeatItemsFromValue(next[field.id]);
        if (existing.length >= field.repeating.minEntries) continue;
        next[field.id] = createInitialRepeatItems(field, () => crypto.randomUUID());
        changed = true;
      }
      return changed ? next : current;
    });
  }, [definition.fields]);

  const runtime = useMemo(() => evaluateFormRuntime(definition, values), [definition, values]);
  const navigation = formNavigationState(definition, pageId);
  const page = definition.pages.find((candidate) => candidate.id === navigation.pageId);
  const sectionIds = new Set(page?.sectionIds ?? []);
  const sections = definition.sections.filter((section) => sectionIds.has(section.id));
  const nested = useMemo(
    () => new Set(definition.fields.flatMap((field) => nestedFieldIds(field))),
    [definition.fields],
  );

  const clearFieldIssue = (fieldId: string, repeatItemId?: string) => {
    setIssues((current) =>
      current.filter(
        (issue) =>
          issue.fieldId !== fieldId ||
          (repeatItemId !== undefined && issue.repeatItemId !== repeatItemId),
      ),
    );
  };

  const setValue = (fieldId: string, value: unknown) => {
    setValues((current) => ({ ...current, [fieldId]: value }));
    clearFieldIssue(fieldId);
  };

  const renderField = (
    field: TemplateField,
    valueRecord: Readonly<Record<string, unknown>>,
    update: (fieldId: string, value: unknown) => void,
    repeatContext?: { parentId: string; itemId: string },
  ): ReactNode => {
    const state = repeatContext
      ? runtime.repeats[repeatContext.parentId]?.items.find(
          (item) => item.itemId === repeatContext.itemId,
        )?.fields[field.id]
      : runtime.fields[field.id];
    if (state && !state.visible) return null;

    const value = valueRecord[field.id];
    const required = Boolean(state?.required);
    const fieldIssue = issues.find(
      (issue) =>
        issue.fieldId === field.id &&
        (repeatContext
          ? issue.repeatItemId === repeatContext.itemId
          : issue.repeatItemId === undefined),
    );
    const issueId = fieldIssue
      ? `preview-error-${repeatContext?.itemId ?? 'top'}-${field.id}`
      : undefined;

    if (field.type === 'heading') {
      return (
        <FieldShell key={field.id} field={field}>
          <h4>{field.label}</h4>
        </FieldShell>
      );
    }
    if (field.type === 'instruction_text') {
      return (
        <FieldShell key={field.id} field={field}>
          <p>{field.label}</p>
        </FieldShell>
      );
    }
    if (field.type === 'divider') {
      return <hr key={field.id} className="runtime-divider" />;
    }
    if (field.type === 'calculated_field' || field.type === 'system_field') {
      return (
        <FieldShell key={field.id} field={field}>
          <span className="field-label">{field.label}</span>
          <output className="runtime-output">
            {field.type === 'system_field'
              ? systemFieldSourceLabel(field.systemSource)
              : valueAsString(repeatContext ? value : runtime.values[field.id]) || '—'}
          </output>
        </FieldShell>
      );
    }
    if (field.type === 'repeating_section' && field.repeating) {
      const repeating = field.repeating;
      const items = repeatItemsFromValue(runtime.values[field.id]);
      return (
        <fieldset
          key={field.id}
          className="runtime-repeat"
          aria-invalid={Boolean(fieldIssue) || undefined}
          aria-describedby={issueId}
        >
          <legend>{field.label}</legend>
          {items.length === 0 ? <p className="help-text">No entries yet.</p> : null}
          {items.map((item, index) => {
            const itemRuntime = runtime.repeats[field.id]?.items.find(
              (candidate) => candidate.itemId === item.id,
            );
            return (
              <div className="runtime-repeat-item" key={item.id}>
                <div className="runtime-repeat-heading">
                  <strong>Entry {index + 1}</strong>
                  <button
                    type="button"
                    className="plain"
                    onClick={() => {
                      const result = removeRepeatItem(field, items, item.id);
                      if (result.changed) setValue(field.id, result.items);
                    }}
                    disabled={items.length <= repeating.minEntries}
                  >
                    Remove
                  </button>
                </div>
                {repeating.fieldIds.map((childId) => {
                  const child = definition.fields.find((candidate) => candidate.id === childId);
                  if (!child) return null;
                  const childValues = itemRuntime?.values ?? item.values;
                  return renderField(
                    child,
                    childValues,
                    (nestedFieldId, nestedValue) => {
                      const nextItems = items.map((candidate) =>
                        candidate.id === item.id
                          ? {
                              ...candidate,
                              values: { ...candidate.values, [nestedFieldId]: nestedValue },
                            }
                          : candidate,
                      );
                      setValue(field.id, nextItems);
                      clearFieldIssue(nestedFieldId, item.id);
                    },
                    { parentId: field.id, itemId: item.id },
                  );
                })}
              </div>
            );
          })}
          <button
            type="button"
            className="secondary"
            onClick={() => {
              const result = addRepeatItem(field, items, { id: crypto.randomUUID(), values: {} });
              if (result.changed) setValue(field.id, result.items);
            }}
            disabled={items.length >= repeating.maxEntries}
          >
            {repeating.addAnotherLabel ?? 'Add another'}
          </button>
          {fieldIssue ? (
            <p id={issueId} className="runtime-error">
              {fieldIssue.message}
            </p>
          ) : null}
        </fieldset>
      );
    }
    if (field.type === 'table_grid' && field.grid) {
      return (
        <FieldShell key={field.id} field={field}>
          <span className="field-label">{field.label}</span>
          <div className="table-wrap">
            <table className="runtime-grid">
              <thead>
                <tr>
                  {field.grid.columns.map((column) => (
                    <th key={column.id}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {field.grid.columns.map((column) => (
                    <td key={column.id}>
                      {column.fieldIds.map((childId) => {
                        const child = definition.fields.find(
                          (candidate) => candidate.id === childId,
                        );
                        return child ? renderField(child, valueRecord, update) : null;
                      })}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </FieldShell>
      );
    }

    if (field.type === 'address') {
      const address = addressValue(value);
      const updateAddress = (key: keyof AddressValue, nextValue: string) =>
        update(field.id, { ...address, [key]: nextValue });
      return (
        <fieldset
          key={field.id}
          className="runtime-address"
          aria-invalid={Boolean(fieldIssue) || undefined}
          aria-describedby={issueId}
        >
          <legend>
            {field.label}
            {required ? ' *' : ''}
          </legend>
          <div className="runtime-address-fields">
            <input
              aria-label={`${field.label} address line 1`}
              value={address.line1}
              placeholder="Address line 1"
              onChange={(event) => updateAddress('line1', event.target.value)}
            />
            <input
              aria-label={`${field.label} address line 2`}
              value={address.line2}
              placeholder="Address line 2"
              onChange={(event) => updateAddress('line2', event.target.value)}
            />
            <input
              aria-label={`${field.label} town or city`}
              value={address.townCity}
              placeholder="Town / City"
              onChange={(event) => updateAddress('townCity', event.target.value)}
            />
            <input
              aria-label={`${field.label} county`}
              value={address.county}
              placeholder="County"
              onChange={(event) => updateAddress('county', event.target.value)}
            />
            <input
              aria-label={`${field.label} postcode`}
              value={address.postcode}
              placeholder="Postcode"
              onChange={(event) => updateAddress('postcode', event.target.value)}
            />
          </div>
          <p className="help-text">
            Postcode lookup and automatic address selection will use the production address service.
          </p>
          {fieldIssue ? (
            <p id={issueId} className="runtime-error">
              {fieldIssue.message}
            </p>
          ) : null}
        </fieldset>
      );
    }

    if (
      [
        'file_upload',
        'single_photograph',
        'multiple_photographs',
        'signature',
        'gps',
        'map_location',
      ].includes(field.type)
    ) {
      return (
        <FieldShell key={field.id} field={field}>
          <span className="field-label">
            {field.label}
            {required ? ' *' : ''}
          </span>
          <div className="runtime-capture-placeholder">
            <strong>{deferredCaptureText(field)}</strong>
            <span>
              Runtime capture is configured here and enabled in the media/signature phase.
            </span>
          </div>
          {fieldIssue ? (
            <p id={issueId} className="runtime-error">
              {fieldIssue.message}
            </p>
          ) : null}
        </FieldShell>
      );
    }

    const label = (
      <label htmlFor={`preview-${repeatContext?.itemId ?? 'top'}-${field.id}`}>
        {field.label}
        {required ? ' *' : ''}
      </label>
    );
    const inputId = `preview-${repeatContext?.itemId ?? 'top'}-${field.id}`;

    if (field.type === 'multi_line_text') {
      return (
        <FieldShell key={field.id} field={field}>
          {label}
          <textarea
            id={inputId}
            value={valueAsString(value)}
            placeholder={field.placeholder}
            onChange={(event) => update(field.id, event.target.value)}
            aria-invalid={Boolean(fieldIssue) || undefined}
            aria-describedby={issueId}
          />
          {fieldIssue ? (
            <p id={issueId} className="runtime-error">
              {fieldIssue.message}
            </p>
          ) : null}
        </FieldShell>
      );
    }

    if (['dropdown', 'radio_buttons', 'checkboxes'].includes(field.type)) {
      const options = field.options ?? [];
      if (field.type === 'dropdown') {
        return (
          <FieldShell key={field.id} field={field}>
            {label}
            <select
              id={inputId}
              value={valueAsString(value)}
              onChange={(event) => update(field.id, event.target.value)}
              aria-invalid={Boolean(fieldIssue) || undefined}
              aria-describedby={issueId}
            >
              <option value="">Select…</option>
              {options.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldIssue ? (
              <p id={issueId} className="runtime-error">
                {fieldIssue.message}
              </p>
            ) : null}
          </FieldShell>
        );
      }
      return (
        <fieldset
          key={field.id}
          className="runtime-choice-group"
          aria-invalid={Boolean(fieldIssue) || undefined}
          aria-describedby={issueId}
        >
          <legend>
            {field.label}
            {required ? ' *' : ''}
          </legend>
          {options.length === 0 ? (
            <p className="help-text">Managed reference choices load at job runtime.</p>
          ) : null}
          {options.map((option) => {
            const selected = choiceValues(value);
            const checked =
              field.type === 'radio_buttons'
                ? value === option.value
                : selected.includes(option.value);
            return (
              <label key={option.id} className="runtime-choice">
                <input
                  type={field.type === 'radio_buttons' ? 'radio' : 'checkbox'}
                  name={field.type === 'radio_buttons' ? inputId : undefined}
                  checked={checked}
                  onChange={(event) => {
                    if (field.type === 'radio_buttons') {
                      update(field.id, event.target.checked ? option.value : '');
                      return;
                    }
                    const current = choiceValues(value);
                    update(
                      field.id,
                      event.target.checked
                        ? [...current, option.value]
                        : current.filter((entry) => entry !== option.value),
                    );
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
          {fieldIssue ? (
            <p id={issueId} className="runtime-error">
              {fieldIssue.message}
            </p>
          ) : null}
        </fieldset>
      );
    }

    if (field.type === 'toggle') {
      return (
        <FieldShell key={field.id} field={field}>
          <label className="runtime-choice">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) => update(field.id, event.target.checked)}
              aria-invalid={Boolean(fieldIssue) || undefined}
              aria-describedby={issueId}
            />
            <span>{field.label}</span>
          </label>
          {fieldIssue ? (
            <p id={issueId} className="runtime-error">
              {fieldIssue.message}
            </p>
          ) : null}
        </FieldShell>
      );
    }

    const inputType =
      field.type === 'email'
        ? 'email'
        : field.type === 'telephone'
          ? 'tel'
          : field.type === 'date'
            ? 'date'
            : field.type === 'time'
              ? 'time'
              : field.type === 'date_time'
                ? 'datetime-local'
                : ['integer', 'decimal', 'currency'].includes(field.type)
                  ? 'number'
                  : 'text';

    return (
      <FieldShell key={field.id} field={field}>
        {label}
        <input
          id={inputId}
          type={inputType}
          step={field.type === 'integer' ? '1' : inputType === 'number' ? 'any' : undefined}
          value={valueAsString(value)}
          placeholder={field.placeholder}
          onChange={(event) => update(field.id, event.target.value)}
          aria-invalid={Boolean(fieldIssue) || undefined}
          aria-describedby={issueId}
        />
        {fieldIssue ? (
          <p id={issueId} className="runtime-error">
            {fieldIssue.message}
          </p>
        ) : null}
      </FieldShell>
    );
  };

  const issueMessage = (issue: FieldValidationIssue): string => {
    if (!issue.repeatFieldId || !issue.repeatItemId) return issue.message;
    const items = runtime.repeats[issue.repeatFieldId]?.items ?? [];
    const index = items.findIndex((item) => item.itemId === issue.repeatItemId);
    return index >= 0 ? `Entry ${index + 1}: ${issue.message}` : issue.message;
  };

  return (
    <div className={`runtime-preview runtime-preview--${mode}`}>
      <div className="runtime-preview-frame">
        <div
          className="runtime-progress"
          aria-label={`Page ${navigation.pageIndex + 1} of ${navigation.pageCount}`}
        >
          <span>
            Page {navigation.pageIndex + 1} of {navigation.pageCount}
          </span>
          <progress value={navigation.pageIndex + 1} max={Math.max(navigation.pageCount, 1)} />
        </div>

        <h3>{page?.title ?? 'Form preview'}</h3>

        {runtime.warnings.map((warning) => (
          <div
            className="alert alert--warning"
            role="status"
            key={`${warning.ruleId}-${warning.repeatItemId ?? 'top'}`}
          >
            <div>{warning.message}</div>
          </div>
        ))}

        {runtime.submissionBlocks.map((block) => (
          <div
            className="alert alert--error"
            role="alert"
            key={`${block.ruleId}-${block.repeatItemId ?? 'top'}`}
          >
            <div>{block.message}</div>
          </div>
        ))}

        {issues.length > 0 ? (
          <div className="runtime-error-summary" role="alert" aria-live="assertive">
            <strong>Check this page</strong>
            <ul>
              {issues.map((issue) => (
                <li key={`${issue.fieldId}-${issue.repeatItemId ?? 'top'}-${issue.code}`}>
                  {issueMessage(issue)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {sections.map((section) => (
          <section className="runtime-section" key={section.id}>
            <h4>{section.title}</h4>
            {section.fieldIds.map((fieldId) => {
              if (nested.has(fieldId)) return null;
              const field = definition.fields.find((candidate) => candidate.id === fieldId);
              return field ? renderField(field, runtime.values, setValue) : null;
            })}
          </section>
        ))}

        <div className="runtime-navigation">
          <button
            type="button"
            className="secondary"
            disabled={!navigation.canGoBack}
            onClick={() => {
              const previous = previousFormPage(definition, navigation.pageId);
              setPageId(previous.pageId);
              setIssues([]);
            }}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!navigation.canGoForward}
            onClick={() => {
              const attempt = advanceFormPage(definition, runtime, navigation.pageId);
              setIssues(attempt.issues);
              if (attempt.moved) setPageId(attempt.state.pageId);
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
